import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { 
  CheckCircle, 
  XCircle, 
  Eye, 
  Download, 
  Image as ImageIcon,
  Clock,
  Package,
  Zap,
  TrendingUp,
  X,
  RefreshCw
} from 'lucide-react';
import { Job, BatchStatus } from '../../services/job';

interface ApprovalInterfaceProps {
  jobs: Job[];
  onApprove: (jobId: string) => Promise<void>;
  onReject: (jobId: string, reason?: string) => Promise<void>;
  onBulkApprove: (jobIds: string[]) => Promise<void>;
  onBulkReject?: (jobIds: string[], reason?: string) => Promise<void>;
  loading?: boolean;
  batchStatus?: BatchStatus | null;
  canApprove?: boolean;
  canReject?: boolean;
}

export const ApprovalInterface: React.FC<ApprovalInterfaceProps> = ({
  jobs,
  onApprove,
  onReject,
  onBulkApprove,
  onBulkReject,
  loading = false,
  batchStatus,
  canApprove = true,
  canReject = true
}) => {
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [rejectingJob, setRejectingJob] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<string>>(new Set());
  const [processingJobs, setProcessingJobs] = useState<Set<string>>(new Set());
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  
  // Image viewer modal
  const [imageViewerModal, setImageViewerModal] = useState<{
    isOpen: boolean;
    originalUrl: string;
    enhancedUrl: string;
    title: string;
    jobId: string;
    productName: string;
    imageIndex: number;
  }>({
    isOpen: false,
    originalUrl: '',
    enhancedUrl: '',
    title: '',
    jobId: '',
    productName: '',
    imageIndex: 0
  });
  
  // Session progress tracking
  const [sessionStartTotal, setSessionStartTotal] = useState<number>(0);
  const [sessionProcessedCount, setSessionProcessedCount] = useState<number>(0);

  // Group jobs by product
  const jobsByProduct = jobs.reduce((groups, job) => {
    const productId = job.product_id;
    if (!groups[productId]) {
      groups[productId] = {
        product: job.products,
        store: job.stores,
        jobs: []
      };
    }
    groups[productId].jobs.push(job);
    return groups;
  }, {} as Record<string, { product: any; store: any; jobs: Job[] }>);

  const productGroups = Object.values(jobsByProduct);

  // Reset selections when jobs change
  useEffect(() => {
    setSelectedJobs(new Set());
  }, [jobs]);

  const handleJobSelection = (jobId: string, checked: boolean) => {
    const newSelected = new Set(selectedJobs);
    if (checked) {
      newSelected.add(jobId);
    } else {
      newSelected.delete(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedJobs.size === jobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(jobs.map(job => job.id)));
    }
  };

  const handleProductSelection = (productId: string, checked: boolean) => {
    const productGroup = jobsByProduct[productId];
    if (!productGroup) return;

    const newSelected = new Set(selectedJobs);
    productGroup.jobs.forEach(job => {
      if (checked) {
        newSelected.add(job.id);
      } else {
        newSelected.delete(job.id);
      }
    });
    setSelectedJobs(newSelected);
  };


  const handleProductApprove = async (productId: string) => {
    const productGroup = jobsByProduct[productId];
    if (!productGroup) return;
    
    const jobIds = productGroup.jobs.map(job => job.id);
    await onBulkApprove(jobIds);
  };

  const isProductSelected = (productId: string) => {
    const productGroup = jobsByProduct[productId];
    if (!productGroup) return false;
    return productGroup.jobs.every(job => selectedJobs.has(job.id));
  };

  const isProductPartiallySelected = (productId: string) => {
    const productGroup = jobsByProduct[productId];
    if (!productGroup) return false;
    const selectedCount = productGroup.jobs.filter(job => selectedJobs.has(job.id)).length;
    return selectedCount > 0 && selectedCount < productGroup.jobs.length;
  };

  const handleIndividualApprove = async (jobId: string) => {
    setProcessingJobs(prev => new Set(prev).add(jobId));
    try {
      await onApprove(jobId);
    } finally {
      setProcessingJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  const handleIndividualReject = async (jobId: string) => {
    setProcessingJobs(prev => new Set(prev).add(jobId));
    try {
      await onReject(jobId, rejectReason);
      setRejectingJob(null);
      setRejectReason('');
    } finally {
      setProcessingJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  const handleBulkApprove = async () => {
    if (selectedJobs.size === 0) return;
    await onBulkApprove(Array.from(selectedJobs));
    setSelectedJobs(new Set());
  };

  const handleBulkReject = async () => {
    if (selectedJobs.size === 0 || !onBulkReject) return;
    await onBulkReject(Array.from(selectedJobs), bulkRejectReason);
    setSelectedJobs(new Set());
    setBulkRejectReason('');
    setShowBulkRejectModal(false);
  };

  const handleRejectAll = () => {
    if (!onBulkReject) return;
    setSelectedJobs(new Set(jobs.map(job => job.id)));
    setShowBulkRejectModal(true);
  };

  const handleImageError = (jobId: string) => {
    setImageLoadErrors(prev => new Set(prev).add(jobId));
  };

  const openImageViewer = (job: Job, productName: string, imageIndex: number) => {
    setImageViewerModal({
      isOpen: true,
      originalUrl: job.original_image_url,
      enhancedUrl: job.processed_image_url,
      title: `${productName} - Image ${imageIndex + 1}`,
      jobId: job.id,
      productName,
      imageIndex
    });
  };

  const closeImageViewer = () => {
    setImageViewerModal({
      isOpen: false,
      originalUrl: '',
      enhancedUrl: '',
      title: '',
      jobId: '',
      productName: '',
      imageIndex: 0
    });
  };

  const formatTransformations = (transformations: any[]) => {
    return transformations.map(t => {
      switch (t.type) {
        case 'background_removal':
          return 'Background Removal';
        case 'resize':
          return `Resize ${t.width}x${t.height}`;
        case 'format_conversion':
          return `Convert to ${t.format?.toUpperCase()}`;
        case 'ai_upscale':
          return 'AI Upscale';
        case 'color_adjustment':
          return 'Color Enhancement';
        default:
          return t.type;
      }
    }).join(', ');
  };

  if (jobs.length === 0) {
    return null; // Let the parent ApprovalPage handle empty state
  }

  return (
    <>
      <div className="space-y-6">
        {/* Progress Overview */}
        {batchStatus && (
          <Card className="bg-gradient-to-r border-2 shadow-md border-primary/30 from-primary/10 to-accent/10">
            <CardHeader>
              <CardTitle className="flex gap-2 items-center text-primary">
                <TrendingUp className="w-5 h-5" />
                Batch Progress Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm font-medium">
                  <span>Overall Batch Progress</span>
                  <span>{batchStatus.progress.percentage}% Complete</span>
                </div>
                <Progress value={batchStatus.progress.percentage} className="h-3" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Completed: {batchStatus.progress.completed} of {batchStatus.total_jobs}</span>
                  <span>Pending Approval: {batchStatus.status_breakdown.processed_pending_approval}</span>
                </div>
              </div>
              <div className="p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <div className="text-sm font-medium text-yellow-900 dark:text-yellow-200">Currently Viewing</div>
                <div className="text-xs text-yellow-700 dark:text-yellow-300">
                  Showing {jobs.length} of {batchStatus.status_breakdown.processed_pending_approval} images that need approval
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Bulk Actions Header */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex gap-2 items-center">
                  <Eye className="w-5 h-5" />
                  Review & Approve Images
                </CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  {productGroups.length} product{productGroups.length !== 1 ? 's' : ''} • {jobs.length} image{jobs.length !== 1 ? 's' : ''} on this page
                  <span className="text-muted-foreground"> - Review and approve each image below</span>
                </p>
              </div>
              <div className="flex gap-3 items-center">
                <div className="flex gap-2 items-center">
                  <input
                    type="checkbox"
                    checked={selectedJobs.size === jobs.length && jobs.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-muted-foreground">
                    Select All ({selectedJobs.size}/{jobs.length})
                  </span>
                </div>
                <Button
                  onClick={handleBulkApprove}
                  disabled={!canApprove || selectedJobs.size === 0 || loading}
                  className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
                  title={!canApprove ? "You don't have permission to approve jobs" : undefined}
                >
                  <CheckCircle className="mr-2 w-4 h-4" />
                  {!canApprove ? 'No Permission' : `Approve Selected (${selectedJobs.size})`}
                </Button>
                {onBulkReject && (
                  <>
                    <Button
                      onClick={() => setShowBulkRejectModal(true)}
                      disabled={!canReject || selectedJobs.size === 0 || loading}
                      variant="outline"
                      className="text-red-700 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-950/30"
                      title={!canReject ? "You don't have permission to reject jobs" : undefined}
                    >
                      <XCircle className="mr-2 w-4 h-4" />
                      {!canReject ? 'No Permission' : `Reject Selected (${selectedJobs.size})`}
                    </Button>
                    <Button
                      onClick={handleRejectAll}
                      disabled={!canReject || jobs.length === 0 || loading}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
                      title={!canReject ? "You don't have permission to reject jobs" : undefined}
                    >
                      <XCircle className="mr-2 w-4 h-4" />
                      {!canReject ? 'No Permission' : 'Reject All'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Product Groups */}
        <div className="space-y-4">
          {productGroups.map((group) => {
            const isSelected = isProductSelected(group.product.id);
            const isPartiallySelected = isProductPartiallySelected(group.product.id);
            
            return (
              <Card key={group.product.id} className="overflow-hidden">
                {/* Product Header */}
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-center">
                    <div className="flex gap-3 items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        ref={(input) => {
                          if (input) input.indeterminate = isPartiallySelected;
                        }}
                        onChange={(e) => handleProductSelection(group.product.id, e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                      <div>
                        <h3 className="font-medium">
                          {group.product.name}
                        </h3>
                        <div className="flex gap-4 items-center mt-1 text-sm text-muted-foreground">
                          <span className="flex gap-1 items-center">
                            <Package className="w-3 h-3" />
                            SKU: {group.product.sku}
                          </span>
                          <span className="flex gap-1 items-center">
                            <ImageIcon className="w-3 h-3" />
                            {group.jobs.length} image{group.jobs.length !== 1 ? 's' : ''}
                          </span>
                          <span className="flex gap-1 items-center">
                            <Zap className="w-3 h-3" />
                            {group.jobs.reduce((sum, job) => sum + (job.transformation_credits || 0), 0)} credits total
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 items-center">
                      <Badge variant="outline">
                        {formatTransformations(group.jobs[0].transformations)}
                      </Badge>
                      <Button
                        onClick={() => handleProductApprove(group.product.id)}
                        size="sm"
                        disabled={!canApprove}
                        className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
                        title={!canApprove ? "You don't have permission to approve jobs" : undefined}
                      >
                        <CheckCircle className="mr-1 w-3 h-3" />
                        {!canApprove ? 'No Permission' : `Approve All (${group.jobs.length})`}
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {/* Always Show Job Details */}
                <CardContent className="pt-0">
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                      {group.jobs.map((job, jobIndex) => (
                        <div key={job.id} className="overflow-hidden bg-background rounded-xl border-2 border-border transition-all duration-200 hover:border-border/80">
                          {/* Card Header */}
                          <div className="flex justify-between items-center p-4 bg-muted/30 border-b border-border">
                            <div className="flex gap-3 items-center">
                              <input
                                type="checkbox"
                                checked={selectedJobs.has(job.id)}
                                onChange={(e) => handleJobSelection(job.id, e.target.checked)}
                                className="w-5 h-5 rounded border-2 border-gray-300 dark:border-gray-600"
                              />
                              <div className="flex gap-2 items-center">
                                <Badge variant="outline" className="font-medium">
                                  Image {jobIndex + 1}
                                  {job.original_image_url.includes('main') || job.original_image_url.includes('primary') ? ' (Main)' : 
                                   job.original_image_url.includes('gallery') ? ' (Gallery)' : ''}
                                </Badge>
                                <div className="px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-950/50 rounded-full">
                                  {job.transformation_credits} credits
                                </div>
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex gap-2 items-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setRejectingJob(job.id)}
                                disabled={!canReject || processingJobs.has(job.id)}
                                className={`${processingJobs.has(job.id) ? 'opacity-50 cursor-not-allowed' : ''} text-red-700 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-950/30`}
                                title={!canReject ? "You don't have permission to reject jobs" : undefined}
                              >
                                <XCircle className="mr-1 w-4 h-4" />
                                {!canReject ? 'No Permission' : 'Reject'}
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleIndividualApprove(job.id)}
                                disabled={!canApprove || processingJobs.has(job.id)}
                                className={`${processingJobs.has(job.id) ? 'opacity-50 cursor-not-allowed' : ''} bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800`}
                                title={!canApprove ? "You don't have permission to approve jobs" : undefined}
                              >
                                {processingJobs.has(job.id) ? (
                                  <>
                                    <RefreshCw className="mr-1 w-4 h-4 animate-spin" />
                                    Approving...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="mr-1 w-4 h-4" />
                                    Approve
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>

                          {/* Image Comparison */}
                          <div className="p-4">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              {/* Original Image */}
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <h6 className="flex gap-1 items-center text-sm font-medium text-foreground">
                                    <ImageIcon className="w-4 h-4 text-muted-foreground" />
                                    Original
                                  </h6>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => window.open(job.original_image_url, '_blank')}
                                    className="px-2 h-6 text-xs text-muted-foreground hover:text-foreground"
                                  >
                                    <Download className="w-3 h-3" />
                                  </Button>
                                </div>
                                <div 
                                  className="overflow-hidden relative h-48 bg-muted/20 rounded-lg border transition-all cursor-pointer hover:ring-2 hover:ring-ring/50"
                                  onClick={() => openImageViewer(job, group.product.name, jobIndex)}
                                >
                                  {imageLoadErrors.has(`${job.id}-original`) ? (
                                    <div className="flex justify-center items-center h-full">
                                      <div className="text-center">
                                        <ImageIcon className="mx-auto mb-2 w-8 h-8 text-muted-foreground" />
                                        <p className="text-xs text-muted-foreground">Failed to load</p>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <img
                                        src={job.original_image_url}
                                        alt={`Original ${group.product.name} - Image ${jobIndex + 1}`}
                                        className="object-contain p-2 w-full h-full"
                                        onError={() => handleImageError(`${job.id}-original`)}
                                      />
                                      {/* Click to enlarge indicator */}
                                      <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 opacity-0 transition-all hover:bg-opacity-10 hover:opacity-100">
                                        <div className="p-2 bg-white bg-opacity-90 rounded-full">
                                          <Eye className="w-4 h-4 text-foreground" />
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Enhanced Image */}
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <h6 className="flex gap-1 items-center text-sm font-medium text-blue-700">
                                    <Zap className="w-4 h-4 text-blue-600" />
                                    Enhanced
                                  </h6>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => window.open(job.processed_image_url, '_blank')}
                                    className="px-2 h-6 text-xs text-blue-600 hover:text-blue-700"
                                  >
                                    <Download className="w-3 h-3" />
                                  </Button>
                                </div>
                                <div 
                                  className="overflow-hidden relative h-48 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800 transition-all cursor-pointer hover:ring-2 hover:ring-blue-300 dark:hover:ring-blue-600"
                                  onClick={() => openImageViewer(job, group.product.name, jobIndex)}
                                >
                                  {imageLoadErrors.has(`${job.id}-processed`) ? (
                                    <div className="flex justify-center items-center h-full">
                                      <div className="text-center">
                                        <ImageIcon className="mx-auto mb-2 w-8 h-8 text-muted-foreground" />
                                        <p className="text-xs text-muted-foreground">Failed to load</p>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <img
                                        src={job.processed_image_url}
                                        alt={`Enhanced ${group.product.name} - Image ${jobIndex + 1}`}
                                        className="object-contain p-2 w-full h-full"
                                        onError={() => handleImageError(`${job.id}-processed`)}
                                      />
                                      {/* Click to enlarge indicator */}
                                      <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 opacity-0 transition-all hover:bg-opacity-10 hover:opacity-100">
                                        <div className="p-2 bg-white bg-opacity-90 rounded-full">
                                          <Eye className="w-4 h-4 text-blue-700" />
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Processing Info */}
                            <div className="pt-3 mt-3 border-t border-border">
                              <div className="flex justify-between items-center text-xs text-muted-foreground">
                                <span className="flex gap-1 items-center">
                                  <Clock className="w-3 h-3" />
                                  {job.processed_at ? new Date(job.processed_at).toLocaleDateString() : 'Processing...'}
                                </span>
                                <span className="font-medium">
                                  {formatTransformations(job.transformations)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Reject Modal */}
        {rejectingJob && (
          <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Reject Image</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Please provide a reason for rejecting this processed image.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Optional: Reason for rejection"
                  value={rejectReason}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRejectReason(e.target.value)}
                  rows={3}
                />
                <div className="flex gap-2 justify-end items-center">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setRejectingJob(null);
                      setRejectReason('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleIndividualReject(rejectingJob)}
                    disabled={processingJobs.has(rejectingJob)}
                    className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
                  >
                    {processingJobs.has(rejectingJob) ? 'Rejecting...' : 'Reject'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bulk Reject Modal */}
        {showBulkRejectModal && (
          <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Reject {selectedJobs.size === jobs.length ? 'All Images' : 'Selected Images'}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {selectedJobs.size === jobs.length 
                    ? `You are about to reject all ${jobs.length} processed images.`
                    : `You are about to reject ${selectedJobs.size} selected images.`
                  }
                  Please provide a reason for rejection.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Optional: Reason for rejection"
                  value={bulkRejectReason}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setBulkRejectReason(e.target.value)}
                  rows={3}
                />
                <div className="flex gap-2 justify-end items-center">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowBulkRejectModal(false);
                      setBulkRejectReason('');
                      if (selectedJobs.size === jobs.length) {
                        setSelectedJobs(new Set()); // Clear selection if it was "reject all"
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleBulkReject}
                    disabled={loading}
                    className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
                  >
                    {loading ? 'Rejecting...' : `Reject ${selectedJobs.size} Image${selectedJobs.size !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Image Viewer Modal - Side by Side Comparison */}
      {imageViewerModal.isOpen && (
        <div className="fixed inset-0 z-50 !mt-0 bg-black bg-opacity-95">
          <div className="flex relative flex-col p-8 w-full h-full">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 mb-4 bg-white bg-opacity-10 rounded-lg backdrop-blur-sm">
              <div className="text-white">
                <h3 className="text-xl font-semibold">{imageViewerModal.title}</h3>
                <p className="text-sm text-gray-300">
                  Side-by-side comparison - Original vs Enhanced
                </p>
              </div>
              <div className="flex gap-3 items-center">
                <a
                  href={imageViewerModal.enhancedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex gap-2 items-center px-3 py-2 text-sm text-white bg-blue-600 bg-opacity-80 rounded-lg transition-colors hover:bg-opacity-100"
                >
                  <Download className="w-4 h-4" />
                  Download Enhanced
                </a>
                <a
                  href={imageViewerModal.originalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex gap-2 items-center px-3 py-2 text-sm text-white bg-gray-600 bg-opacity-80 rounded-lg transition-colors hover:bg-opacity-100 dark:bg-gray-500"
                >
                  <Download className="w-4 h-4" />
                  Download Original
                </a>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={closeImageViewer}
                  className="text-white bg-white bg-opacity-20 border-white border-opacity-30 hover:bg-opacity-30"
                >
                  <X className="mr-2 w-4 h-4" />
                  Close
                </Button>
              </div>
            </div>

            {/* Side-by-Side Image Container */}
            <div className="flex overflow-hidden flex-1 gap-4 p-4 bg-white bg-opacity-5 rounded-lg">
              {/* Original Image */}
              <div className="flex flex-col flex-1">
                <div className="flex justify-between items-center mb-3">
                  <div className="px-4 py-2 text-lg font-medium text-white bg-gray-600 dark:bg-gray-500 rounded-lg">
                    Original Image
                  </div>
                  <a
                    href={imageViewerModal.originalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-1 items-center text-sm text-gray-300 transition-colors hover:text-white"
                  >
                    <Eye className="w-4 h-4" />
                    Full Size
                  </a>
                </div>
                <div className="flex overflow-hidden flex-1 justify-center items-center bg-gray-800 bg-opacity-30 rounded-lg">
                  {imageLoadErrors.has(`${imageViewerModal.jobId}-original`) ? (
                    <div className="text-center text-gray-400">
                      <ImageIcon className="mx-auto mb-4 w-16 h-16" />
                      <p>Failed to load original image</p>
                    </div>
                  ) : (
                    <img
                      src={imageViewerModal.originalUrl}
                      alt={`${imageViewerModal.productName} - Original`}
                      className="object-contain max-w-full max-h-full"
                      style={{ maxHeight: 'calc(100vh - 280px)' }}
                      onError={() => handleImageError(`${imageViewerModal.jobId}-original`)}
                    />
                  )}
                </div>
              </div>

              {/* Enhanced Image */}
              <div className="flex flex-col flex-1">
                <div className="flex justify-between items-center mb-3">
                  <div className="px-4 py-2 text-lg font-medium text-white bg-blue-600 rounded-lg">
                    Enhanced Image
                  </div>
                  <a
                    href={imageViewerModal.enhancedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-1 items-center text-sm text-blue-300 transition-colors hover:text-white"
                  >
                    <Eye className="w-4 h-4" />
                    Full Size
                  </a>
                </div>
                <div className="flex overflow-hidden flex-1 justify-center items-center bg-blue-800 bg-opacity-20 rounded-lg">
                  {imageLoadErrors.has(`${imageViewerModal.jobId}-processed`) ? (
                    <div className="text-center text-blue-300">
                      <ImageIcon className="mx-auto mb-4 w-16 h-16" />
                      <p>Failed to load enhanced image</p>
                    </div>
                  ) : (
                    <img
                      src={imageViewerModal.enhancedUrl}
                      alt={`${imageViewerModal.productName} - Enhanced`}
                      className="object-contain max-w-full max-h-full"
                      style={{ maxHeight: 'calc(100vh - 280px)' }}
                      onError={() => handleImageError(`${imageViewerModal.jobId}-processed`)}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-4 justify-center items-center mt-4">
              <Button
                variant="outline"
                size="lg"
                onClick={async () => {
                  closeImageViewer();
                  setRejectingJob(imageViewerModal.jobId);
                }}
                disabled={!canReject}
                className="px-6 text-white bg-red-600 border-red-600 hover:bg-red-700"
                title={!canReject ? "You don't have permission to reject jobs" : undefined}
              >
                <XCircle className="mr-2 w-5 h-5" />
                {!canReject ? 'No Permission' : 'Reject This Image'}
              </Button>
              <Button
                size="lg"
                onClick={async () => {
                  closeImageViewer();
                  await handleIndividualApprove(imageViewerModal.jobId);
                }}
                className="px-6 bg-green-600 hover:bg-green-700"
                disabled={!canApprove || processingJobs.has(imageViewerModal.jobId)}
                title={!canApprove ? "You don't have permission to approve jobs" : undefined}
              >
                {processingJobs.has(imageViewerModal.jobId) ? (
                  <>
                    <RefreshCw className="mr-2 w-5 h-5 animate-spin" />
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 w-5 h-5" />
                    Approve This Image
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Click outside to close */}
          <div 
            className="absolute inset-0 -z-10"
            onClick={closeImageViewer}
          ></div>
        </div>
      )}
    </>
  );
};

export default ApprovalInterface;